import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

// Pages
import SplashScreen from "./pages/SplashScreen";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CollectionsPage from "./pages/CollectionsPage";
import ContactPage from "./pages/ContactPage";
import BasketPage from "./pages/BasketPage";
import TurnkeyPage from "./pages/TurnkeyPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/AdminDashboard";

// Components
import Header from "./components/Header";
import RequestModal from "./components/RequestModal";
import CookieBanner from "./components/CookieBanner";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Use relative /api paths so it works on any domain (preview, production, custom domain)
export const API = '/api';

// Resolve image URLs - handles both relative (/api/files/...) and absolute (https://...) URLs
export const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return url; // relative URLs work as-is on same domain
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set up axios interceptors
  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem('introzzi_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      config.withCredentials = true;
      return config;
    });
    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && localStorage.getItem('introzzi_token')) {
          localStorage.removeItem('introzzi_token');
          setUser(null);
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem('introzzi_token');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('introzzi_token', response.data.access_token);
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {});
    localStorage.removeItem('introzzi_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// Request Modal Context
const RequestModalContext = createContext(null);

export const useRequestModal = () => useContext(RequestModalContext);

export const RequestModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [product, setProduct] = useState(null);

  const openModal = (productData) => {
    setProduct(productData);
    setIsOpen(true);
    document.body.classList.add('modal-open');
  };

  const closeModal = () => {
    setIsOpen(false);
    setProduct(null);
    document.body.classList.remove('modal-open');
  };

  return (
    <RequestModalContext.Provider value={{ isOpen, product, openModal, closeModal }}>
      {children}
      <RequestModal />
    </RequestModalContext.Provider>
  );
};

// Basket Context
const BasketContext = createContext(null);

export const useBasket = () => useContext(BasketContext);

export const BasketProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('introzzi_basket');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('introzzi_basket', JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product) => {
    setItems(prev => {
      if (prev.some(item => item.id === product.id)) return prev;
      return [...prev, { id: product.id, name: product.name, slug: product.slug, sku: product.sku, cover_image: product.cover_image, collection: product.collection }];
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setItems(prev => prev.filter(item => item.id !== productId));
  }, []);

  const clearBasket = useCallback(() => setItems([]), []);

  const isInBasket = useCallback((productId) => items.some(item => item.id === productId), [items]);

  return (
    <BasketContext.Provider value={{ items, addItem, removeItem, clearBasket, isInBasket, count: items.length }}>
      {children}
    </BasketContext.Provider>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Animated Routes
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
        <Route path="/products" element={<PublicLayout><ProductsPage /></PublicLayout>} />
        <Route path="/products/:slug" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
        <Route path="/collections" element={<PublicLayout><CollectionsPage /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
        <Route path="/basket" element={<PublicLayout><BasketPage /></PublicLayout>} />
        <Route path="/turnkey" element={<PublicLayout><TurnkeyPage /></PublicLayout>} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/*" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
      </Routes>
      </AnimatePresence>
    </>
  );
};

// Public Layout with Header
const PublicLayout = ({ children }) => {
  return (
    <>
      <Header />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.main>
    </>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    // Only show splash if not previously shown in this session
    const hasSeenSplash = sessionStorage.getItem('introzzi_splash_shown');
    return !hasSeenSplash;
  });

  useEffect(() => {
    if (showSplash) {
      const timer = setTimeout(() => {
        setShowSplash(false);
        sessionStorage.setItem('introzzi_splash_shown', 'true');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showSplash]);

  return (
    <div className="min-h-screen bg-black">
      <AuthProvider>
        <BasketProvider>
          <RequestModalProvider>
            <AnimatePresence>
              {showSplash && <SplashScreen key="splash" />}
            </AnimatePresence>
            
            {!showSplash && (
              <BrowserRouter>
                <AnimatedRoutes />
              </BrowserRouter>
            )}
            
            <Toaster position="bottom-right" theme="dark" />
            <CookieBanner />
          </RequestModalProvider>
        </BasketProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
